/**
 * useApproveTask — calls complete-task with action='approve'.
 *
 * Wraps the Edge Function so the supervisor UI can call a single typed
 * mutation. Error handling mirrors useSubmitTask: pull the structured
 * { error, detail } shape out of the function response, translate the
 * known error codes into supervisor-friendly user messages, and surface
 * unknown ones verbatim.
 *
 * The underlying SQL RPC (`approve_cleaning_task`) is the atomic spine —
 * it locks the task row, validates the supervisor's role + org + task
 * state, sets task.approved + room→ready in one Postgres transaction.
 * This hook does not touch any of that; it is a thin transport layer.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

interface ApproveVars {
  taskId: string
}

export interface ApproveResult {
  task_id:          string
  task_status:      'approved'
  room_next_status: 'ready' | 'reserved'
  room: {
    room_id:         string
    previous_status: string
    new_status:      string
  }
}

export function useApproveTask(): UseMutationResult<ApproveResult, AppError, ApproveVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<ApproveResult, AppError, ApproveVars>({
    mutationFn: async ({ taskId }): Promise<ApproveResult> => {
      const { data, error } = await supabase.functions.invoke<ApproveResult>('complete-task', {
        body: {
          task_id: taskId,
          action:  'approve',
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'APPROVE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Approve returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['task',                vars.taskId] })
      void queryClient.invalidateQueries({ queryKey: ['pending-inspections']              })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks']                         })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_STATE':       return 'This task is not in a state that can be approved.'
    case 'FORBIDDEN_ROLE':      return 'Only supervisors and above can approve tasks.'
    case 'FORBIDDEN_ORG':       return 'This task does not belong to your organization.'
    case 'RATE_LIMITED':        return 'Too many approvals in a row. Try again in a moment.'
    case 'BLOCKING_MAINTENANCE':return 'There are unresolved blocking maintenance issues for this room.'
    case 'TASK_NOT_FOUND':      return 'This task could not be found.'
    default:                    return detail || 'Could not approve the task.'
  }
}
